import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, UtensilsCrossed, ChefHat, Salad, Baby } from "lucide-react";
import { IMG, MENU, ASSETS } from "@/data/content";
import { lockScroll, unlockScroll } from "@/lib/scroll";
import { imgProps } from "@/lib/img";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { FleurDivider } from "@/components/Ornament";

const DETAILS = [
  { icon: ChefHat, text: "Власна кухня та шеф-кухар у кожному садочку" },
  { icon: UtensilsCrossed, text: "5-разове збалансоване харчування протягом дня" },
  { icon: Salad, text: "Свіжі сезонні овочі та фрукти щодня" },
  { icon: Baby, text: "Індивідуальне меню з урахуванням алергій та потреб дитини" },
];

function MenuModal({ onClose }) {
  const [day, setDay] = useState(0);
  const current = MENU.days[day];
  return (
    <motion.div
      className="fixed inset-0 z-[90] flex items-center justify-center p-3"
      style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
      data-testid="menu-modal"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-[#030a07]/85 backdrop-blur-md"
        onClick={onClose}
        data-testid="menu-modal-backdrop"
      />
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="relative z-10 w-[95%] max-w-3xl max-h-[85dvh] rounded-3xl glass shadow-[0_0_80px_rgba(230,184,92,0.2)] flex flex-col overflow-hidden"
        data-lenis-prevent
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full glass flex items-center justify-center text-amber-100 hover:text-amber-300 hover:rotate-90 transition-all duration-300 cursor-pointer"
          data-testid="menu-modal-close"
          aria-label="Закрити"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="shrink-0 px-6 sm:px-9 pt-6 sm:pt-8">
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-white text-center pr-8" data-testid="menu-modal-title">
            {MENU.title}
          </h3>
          <p className="text-center text-amber-200/80 text-sm mt-1">{MENU.dates}</p>
          <FleurDivider id="ornMenu" size="w-4 h-4" className="mt-3 opacity-80" />

          <div className="mt-5 flex flex-wrap justify-center gap-2" data-testid="menu-day-tabs">
            {MENU.days.map((d, i) => (
              <button
                key={d.day}
                type="button"
                onClick={() => setDay(i)}
                className={`rounded-full px-4 py-2 text-xs sm:text-sm font-cinzel tracking-wider transition-all duration-300 cursor-pointer ${
                  i === day
                    ? "bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-bold shadow-[0_0_20px_rgba(230,184,92,0.5)]"
                    : "border border-amber-300/30 text-amber-100/80 hover:border-amber-300 hover:text-amber-200"
                }`}
                data-testid={`menu-day-tab-${i}`}
              >
                {d.day}
              </button>
            ))}
          </div>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain magical-scrollbar px-6 sm:px-9 pb-6 sm:pb-8"
          style={{ WebkitOverflowScrolling: "touch" }}
          data-lenis-prevent
          data-testid="menu-scroll-area"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={day}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.35 }}
              className="mt-5 space-y-4"
              data-testid="menu-day-content"
            >
              {current.meals.map((meal) => (
                <div key={meal.name} className="rounded-2xl border border-amber-400/15 bg-white/[0.04] p-4 sm:p-5">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <span className="text-lg leading-none">{meal.emoji}</span>
                    <span className="font-cinzel text-amber-200 text-sm sm:text-base tracking-wider">{meal.name}</span>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {meal.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-slate-100/90">
                        <span className="text-amber-400 mt-0.5 text-[10px]">✦</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

function DetailsModal({ onClose }) {
  return (
    <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-3" data-testid="nutrition-details-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-[#030a07]/85 backdrop-blur-md"
        onClick={onClose}
      />
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="relative z-10 w-[95%] max-w-xl rounded-3xl glass p-6 sm:p-9 shadow-[0_0_80px_rgba(230,184,92,0.2)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center text-amber-100 hover:text-amber-300 hover:rotate-90 transition-all duration-300 cursor-pointer"
          data-testid="nutrition-details-close"
          aria-label="Закрити"
        >
          <X className="w-5 h-5" />
        </button>
        <img
          src={ASSETS.giraffe}
          alt="Помічник — жираф"
          className="absolute top-4 left-4 sm:top-5 sm:left-5 w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover bg-white ring-2 ring-amber-300/60 shadow-[0_6px_20px_rgba(0,0,0,0.4)]"
          loading="lazy"
          decoding="async"
          data-testid="nutrition-details-avatar"
        />
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-white pl-16 sm:pl-20">Деталі харчування</h3>
        <div className="mt-6 space-y-3">
          {DETAILS.map((d, i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5">
              <span className="w-9 h-9 rounded-xl bg-amber-300/15 border border-amber-300/30 flex items-center justify-center shrink-0">
                <d.icon className="w-4 h-4 text-amber-300" />
              </span>
              <span className="text-sm text-slate-100/95">{d.text}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Nutrition() {
  const [modal, setModal] = useState(null);

  const open = (m) => {
    setModal(m);
    lockScroll();
  };
  const close = () => {
    setModal(null);
    unlockScroll();
  };

  const btnClass =
    "group flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-semibold px-7 py-3.5 text-sm sm:text-base shadow-[0_0_28px_rgba(230,184,92,0.45)] hover:shadow-[0_0_42px_rgba(230,184,92,0.75)] transition-shadow duration-300 cursor-pointer";

  return (
    <section id="kharchuvannya" className="relative py-16 sm:py-24 px-4" data-testid="nutrition-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Eyebrow number="08" text="Харчування" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-testid="nutrition-title">
            ХАРЧУВАННЯ — <span className="text-gold-gradient">НЕ «ОДНЕ МЕНЮ ДЛЯ ВСІХ»</span>
          </h2>
          <p className="mt-6 text-slate-200/85 text-sm sm:text-base leading-relaxed max-w-lg">
            Ми враховуємо потреби кожної дитини.
          </p>
          <p className="mt-2 text-amber-200/85 text-sm sm:text-base leading-relaxed max-w-lg" data-testid="nutrition-subnote">
            Професійне обладнання. Кухарі вищої категорії.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button type="button" onClick={() => open("details")} className={btnClass} data-testid="nutrition-details-button">
              Деталі харчування
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button type="button" onClick={() => open("menu")} className={btnClass} data-testid="weekly-menu-button">
              Меню
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="space-y-4 sm:space-y-5">
            {[IMG.foodCollage1, IMG.foodCollage2].map((src, i) => (
              <div
                key={src}
                className="rounded-[2rem] overflow-hidden border border-amber-400/20 shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
              >
                <img
                  {...imgProps(src, "(max-width: 1024px) 92vw, 480px")}
                  alt={`Страви садочка — колаж ${i + 1}`}
                  className="w-full h-56 sm:h-72 object-cover hover:scale-105 transition-transform duration-700"
                  data-testid={`nutrition-photo-${i + 1}`}
                />
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {modal === "menu" && <MenuModal onClose={close} />}
        {modal === "details" && <DetailsModal onClose={close} />}
      </AnimatePresence>
    </section>
  );
}
